import React, { useState, useEffect, useCallback } from 'react'
import DriverAvatar from './DriverAvatar'

const PLAYER_COLORS = [
  '#e53e3e','#3182ce','#38a169','#d69e2e',
  '#805ad5','#dd6b20','#319795','#e53e7a',
  '#4299e1','#48bb78','#ecc94b'
]

interface Assignment { playerName: string; drivers: { number: number; name: string }[] }
interface Prizes { first: number; second: number; third: number }
interface LiveEntry {
  position: number; driverNumber: number; driverName: string
  speed: number; location: string; isDNF: boolean
}
interface LeaderboardData {
  entries: LiveEntry[]; raceStatus: string; timestamp: string; source: string
}

interface Props {
  assignments: Assignment[]
  prizes: Prizes
  onRaceFinish: (leaderboard: LiveEntry[]) => void
  onViewPlayer: (playerIndex: number) => void
}

export default function RaceDayDashboard({ assignments, prizes, onRaceFinish, onViewPlayer }: Props) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardData | null>(null)
  const [lastUpdated, setLastUpdated] = useState('')
  const [polling, setPolling] = useState(false)
  const [error, setError] = useState('')

  const buildDriverMap = useCallback((): Record<number, { playerName: string; idx: number }> => {
    const map: Record<number, { playerName: string; idx: number }> = {}
    assignments.forEach((a, idx) => {
      a.drivers.forEach(d => { map[d.number] = { playerName: a.playerName, idx } })
    })
    return map
  }, [assignments])

  const fetchLive = useCallback(async () => {
    try {
      const res = await fetch('/api/race/live')
      const data: LeaderboardData = await res.json()
      setLeaderboard(data)
      setLastUpdated(new Date().toLocaleTimeString())
      setError('')
    } catch {
      setError('Could not reach race data. Retrying…')
    }
  }, [])

  useEffect(() => {
    fetchLive()
    setPolling(true)
    const interval = setInterval(fetchLive, 10_000)
    return () => clearInterval(interval)
  }, [fetchLive])

  const driverMap = buildDriverMap()

  // Group live entries by player
  const playerData = assignments.map((a, idx) => {
    const entries = (leaderboard?.entries ?? [])
      .filter(e => a.drivers.some(d => d.number === e.driverNumber))
      .sort((x, y) => x.position - y.position)

    const dnfCount = entries.filter(e => e.isDNF).length

    // Calculate current payout
    let currentPayout = 0
    entries.forEach(e => {
      if (e.position === 1) currentPayout += prizes.first
      if (e.position === 2) currentPayout += prizes.second
      if (e.position === 3) currentPayout += prizes.third
    })

    return { ...a, idx, entries, dnfCount, currentPayout }
  })

  const isMock = leaderboard?.source === 'mock'

  return (
    <div>
      <div className="race-header">
        <div className="section-title" style={{ margin: 0 }}>🏎️ Live Race</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
          <span className={`race-status-badge ${isMock ? 'status-mock' : 'status-live'}`}>
            {isMock ? '🎮 Demo Mode' : '🔴 LIVE'}
          </span>
          {lastUpdated && (
            <span className="last-updated">Updated {lastUpdated}</span>
          )}
          <button
            className="btn btn-secondary"
            style={{ padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
            onClick={() => onRaceFinish(leaderboard?.entries ?? [])}
          >
            🏁 End Race & See Results
          </button>
        </div>
      </div>

      {isMock && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          Running in demo mode with simulated data. On race day (May 24) the app will
          automatically switch to live IndyCar data.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {/* Prize strip */}
      <div className="race-prize-strip" style={{ marginBottom: '1.2rem' }}>
        <div className="prize-strip-label">💰 PRIZES</div>
        {[
          { label: '🥇 1st', val: prizes.first },
          { label: '🥈 2nd', val: prizes.second },
          { label: '🥉 3rd', val: prizes.third },
        ].map(({ label, val }) => (
          <div className="prize-strip-item" key={label}>
            <div className="prize-strip-pos">{label}</div>
            <div className="prize-strip-val">${val}</div>
          </div>
        ))}
      </div>

      {!leaderboard ? (
        <div className="loading-spinner" />
      ) : (
        <div className="race-players-grid">
          {playerData
            .sort((a, b) => {
              const aBest = a.entries[0]?.position ?? 999
              const bBest = b.entries[0]?.position ?? 999
              return aBest - bBest
            })
            .map(player => (
            <div
              className="race-player-card"
              key={player.playerName}
              style={{ borderColor: PLAYER_COLORS[player.idx % PLAYER_COLORS.length] }}
            >
              <div className="race-player-header">
                <div className="race-player-name"
                  style={{ color: PLAYER_COLORS[player.idx % PLAYER_COLORS.length] }}>
                  {player.playerName}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {player.currentPayout > 0 && (
                    <span style={{ color: '#d4a017', fontWeight: 700, fontSize: '0.85rem' }}>
                      +${player.currentPayout}
                    </span>
                  )}
                  {player.dnfCount > 0 && (
                    <span className="race-dnf-badge">{player.dnfCount} DNF</span>
                  )}
                  <button
                    onClick={() => onViewPlayer(player.idx)}
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.7)', borderRadius: 5, padding: '0.2rem 0.55rem',
                      fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'Montserrat,sans-serif' }}
                  >
                    🗺 View
                  </button>
                </div>
              </div>

              <div className="race-drivers-list">
                {player.entries.length === 0 ? (
                  <div style={{ padding: '0.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                    Loading…
                  </div>
                ) : (
                  player.entries.map(entry => (
                    <div
                      key={entry.driverNumber}
                      className={`race-driver-row${entry.isDNF ? ' dnf' : ''}${entry.position <= 3 ? ' top3' : ''}`}
                      title={`${entry.driverName} · P${entry.position}${entry.isDNF ? ' (DNF)' : ` · ${entry.speed.toFixed(1)} mph`}`}
                    >
                      <div className={`pos-badge pos-${Math.min(entry.position, 4)}`}>
                        {entry.position <= 3 ? ['🥇','🥈','🥉'][entry.position - 1] : entry.position}
                      </div>
                      <DriverAvatar
                        name={entry.driverName}
                        number={entry.driverNumber}
                        color={PLAYER_COLORS[player.idx % PLAYER_COLORS.length]}
                        size={30}
                      />
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.driverName}
                      </div>
                      {entry.isDNF
                        ? <span className="dnf-tag">DNF</span>
                        : <span className="speed-tag">{entry.speed.toFixed(1)} mph</span>
                      }
                      <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {entry.location}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
